/**
 * 토큰 저장소의 단일 출처.
 *
 * "로그인 유지" 를 고르면 localStorage(zustand persist), 아니면 브라우저 종료 시 사라지는
 * session cookie 에 담는다. 저장 위치와 키를 아는 코드를 여기로 모아 두는 이유는
 * accountStore(쓰기)와 axios 인터셉터(읽기)가 서로 다른 레이아웃을 가정하는 일을 막기 위함이다.
 */

export const AUTH_STORAGE_KEY = "alon_account";
export const AUTH_COOKIE_KEY = "alon_account_session";

/** session cookie (만료 시간 없음 = 브라우저 종료 시 삭제) */
export const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Strict`;
};

export const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

export const deleteCookie = (name: string) => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const parseJson = (raw: string | null, source: string): unknown => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    console.warn(`Failed to parse stored auth from ${source}`, e);
    return null;
  }
};

const pickString = (value: unknown, field: string): string | null => {
  if (!value || typeof value !== "object") return null;
  const found = (value as Record<string, unknown>)[field];
  return typeof found === "string" && found ? found : null;
};

type TokenField = "access_token" | "refresh_token";

/**
 * 저장된 토큰을 읽는다. localStorage(persist) 를 먼저 보고 없으면 session cookie 를 본다.
 *
 * 스토어의 메모리 상태가 아니라 저장소를 직접 읽는다 — 다른 탭이 갱신한 토큰은 이 탭의
 * 스토어에 반영되지 않으므로, 스토어만 믿으면 멀쩡한 토큰을 두고 갱신을 다시 돌게 된다.
 */
export const readStoredToken = (field: TokenField): string | null => {
  // zustand persist 는 { state: {...}, version } 형태로 감싸 저장한다
  const persisted = parseJson(localStorage.getItem(AUTH_STORAGE_KEY), "localStorage");
  const fromStorage = pickString(
    (persisted as { state?: unknown } | null)?.state,
    field,
  );
  if (fromStorage) return fromStorage;

  return pickString(parseJson(getCookie(AUTH_COOKIE_KEY), "cookie"), field);
};
